import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../services/api';

const BulkImportProducts = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [excelFile, setExcelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);   // { summary, results }
  const [imported, setImported] = useState(null);  // rapport final
  const excelRef = useRef(null);
  const zipRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      setError('');
      const res = await productsAPI.bulkTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_produits_main2main.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(t('bulkImport.errDownload'));
    } finally {
      setDownloading(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!excelFile) { setError(t('bulkImport.errSelectExcel')); return; }
    setError('');
    setLoading(true);
    if (dryRun) { setPreview(null); setImported(null); }
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      if (zipFile) fd.append('images', zipFile);
      fd.append('dryRun', dryRun ? 'true' : 'false');
      const res = await productsAPI.bulkImport(fd);
      if (dryRun) setPreview(res.data);
      else { setImported(res.data); setPreview(null); }
    } catch (e) {
      setError(e.response?.data?.message || t('bulkImport.errProcessing'));
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setExcelFile(null); setZipFile(null); setPreview(null); setImported(null); setError('');
    if (excelRef.current) excelRef.current.value = '';
    if (zipRef.current) zipRef.current.value = '';
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <h2 style={{ margin: 0 }}>{t('bulkImport.title')}</h2>
        <button className="btn-secondary" onClick={() => navigate('/my-products')}>{t('bulkImport.backToProducts')}</button>
      </div>

      {/* Étape 1 : modèle */}
      <div style={styles.card}>
        <h3 style={styles.step}>{t('bulkImport.step1Title')}</h3>
        <p style={styles.muted}>{t('bulkImport.step1Desc')}</p>
        <button className="btn-primary" onClick={handleDownloadTemplate} disabled={downloading}>
          {downloading ? t('bulkImport.downloading') : t('bulkImport.downloadBtn')}
        </button>
      </div>

      {/* Étape 2 : fichiers */}
      <div style={styles.card}>
        <h3 style={styles.step}>{t('bulkImport.step2Title')}</h3>
        <div style={styles.field}>
          <label style={styles.label}>{t('bulkImport.excelLabel')}</label>
          <input ref={excelRef} type="file" accept=".xlsx"
            onChange={(e) => setExcelFile(e.target.files[0] || null)} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>{t('bulkImport.zipLabel')}</label>
          <input ref={zipRef} type="file" accept=".zip"
            onChange={(e) => setZipFile(e.target.files[0] || null)} />
          <small style={styles.muted}>{t('bulkImport.zipHint')}</small>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="btn-secondary" onClick={() => runImport(true)} disabled={loading || !excelFile}>
            {loading ? t('bulkImport.analyzing') : t('bulkImport.previewBtn')}
          </button>
          {(excelFile || zipFile) && (
            <button className="btn-secondary" onClick={resetAll} disabled={loading}>{t('bulkImport.reset')}</button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Étape 3 : prévisualisation */}
      {preview && (
        <div style={styles.card}>
          <h3 style={styles.step}>{t('bulkImport.step3Title')}</h3>
          <div style={styles.summaryRow}>
            <SummaryPill label={t('bulkImport.total')} value={preview.summary.total} color="#2b6cb0" />
            <SummaryPill label={t('bulkImport.valid')} value={preview.summary.valid} color="#2f855a" />
            <SummaryPill label={t('bulkImport.invalid')} value={preview.summary.invalid} color="#c53030" />
          </div>
          <ResultsTable results={preview.results} />
          <div style={{ marginTop: 16 }}>
            {preview.summary.valid > 0 ? (
              <button className="btn-primary" onClick={() => runImport(false)} disabled={loading}>
                {loading ? t('bulkImport.importing') : t('bulkImport.importBtn', { count: preview.summary.valid })}
              </button>
            ) : (
              <p style={{ color: '#c53030' }}>{t('bulkImport.noValid')}</p>
            )}
            {preview.summary.invalid > 0 && preview.summary.valid > 0 && (
              <p style={styles.muted}>{t('bulkImport.invalidIgnored', { count: preview.summary.invalid })}</p>
            )}
          </div>
        </div>
      )}

      {/* Étape 4 : rapport final */}
      {imported && (
        <div style={styles.card}>
          <h3 style={styles.step}>{t('bulkImport.doneTitle')}</h3>
          <div style={styles.summaryRow}>
            <SummaryPill label={t('bulkImport.created')} value={imported.summary.created} color="#2f855a" />
            <SummaryPill label={t('bulkImport.ignoredErrors')} value={imported.summary.invalid} color="#c53030" />
          </div>
          <p style={styles.muted}>{t('bulkImport.publishedMsg', { count: imported.summary.created })}</p>
          <ResultsTable results={imported.results} />
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button className="btn-primary" onClick={() => navigate('/my-products')}>{t('bulkImport.viewProducts')}</button>
            <button className="btn-secondary" onClick={resetAll}>{t('bulkImport.newImport')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryPill = ({ label, value, color }) => (
  <div style={{ ...styles.pill, borderColor: color }}>
    <span style={{ fontSize: 22, fontWeight: 700, color }}>{value}</span>
    <span style={styles.muted}>{label}</span>
  </div>
);

const ResultsTable = ({ results }) => {
  const { t } = useTranslation();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>{t('bulkImport.colRow')}</th>
            <th style={styles.th}>{t('bulkImport.colTitle')}</th>
            <th style={styles.th}>{t('bulkImport.colCategory')}</th>
            <th style={styles.th}>{t('bulkImport.colPhotos')}</th>
            <th style={styles.th}>{t('bulkImport.colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.valid ? 'transparent' : 'rgba(197,48,48,0.06)' }}>
              <td style={styles.td}>{r.row}</td>
              <td style={styles.td}>{r.title}</td>
              <td style={styles.td}>{r.category}</td>
              <td style={styles.td}>{r.imageCount}</td>
              <td style={styles.td}>
                {r.valid
                  ? <span style={{ color: '#2f855a', fontWeight: 600 }}>{t('bulkImport.statusValid')}</span>
                  : <span style={{ color: '#c53030' }}>✗ {r.errors.join(' · ')}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const styles = {
  container: { maxWidth: 1000, margin: '0 auto', padding: '20px 16px 60px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  card: { background: 'var(--card-bg, #fff)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  step: { marginTop: 0, marginBottom: 8 },
  muted: { color: '#718096', fontSize: 13 },
  field: { marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontWeight: 600, fontSize: 14 },
  summaryRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  pill: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, padding: '10px 16px', border: '2px solid', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid rgba(0,0,0,0.1)', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,0.06)', verticalAlign: 'top' }
};

export default BulkImportProducts;
